import type { ReactNode } from 'react'
import { Blocks } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, Outlet, useLocation } from 'react-router'
import { EmptyState, ForbiddenState, FullPageLoader } from '@/components/feedback/States'
import { Button } from '@/components/ui/Button'
import { SCHOOL_MODULE_META, type SchoolModuleKey } from '@/features/school/modules'
import { tAny } from '@/i18n'
import { portalHome, type Permission, type Portal } from './types'
import { useAuth } from './useAuth'

/** Redirects anonymous visitors to the portal's login screen, remembering where they were going. */
export function RequireAuth({ portal }: { portal: Portal }) {
  const { status, user, portal: activePortal } = useAuth()
  const location = useLocation()

  if (status === 'loading') return <FullPageLoader />
  if (status === 'anonymous' || !user) {
    return (
      <Navigate
        to={`/login?portal=${portal}&next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    )
  }
  if (!user.portals.includes(portal)) {
    return <Navigate to={portalHome[user.portals[0] ?? 'family']} replace />
  }
  // A user signed into one portal who navigates to another portal they may use is sent to sign in there.
  if (activePortal !== portal) {
    return <Navigate to={`/login?portal=${portal}`} replace />
  }
  return <Outlet />
}

/** Renders a forbidden state instead of the page when the permission is missing. */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission
  children?: ReactNode
}) {
  const { can, portal } = useAuth()
  if (!can(permission)) return <ForbiddenState homeHref={portalHome[portal ?? 'admin']} />
  return children ?? <Outlet />
}

export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission
  children: ReactNode
  fallback?: ReactNode
}) {
  const { can } = useAuth()
  return can(permission) ? children : fallback
}

/** Shows a "not enabled" state instead of a module's pages when the school has that module off. */
export function RequireModule({ module }: { module: SchoolModuleKey }) {
  const { t } = useTranslation()
  const { hasModule, portal } = useAuth()
  if (hasModule(module)) return <Outlet />
  return (
    <div className="pt-10">
      <EmptyState
        icon={<Blocks />}
        title={t('This module is not enabled')}
        description={t(
          '{{module}} is not part of your school’s plan. Contact the platform support to turn it on.',
          {
            module: tAny(SCHOOL_MODULE_META[module].label),
          },
        )}
        action={
          <Button asChild variant="secondary">
            <Link to={portalHome[portal ?? 'admin']}>{t('Back to home')}</Link>
          </Button>
        }
      />
    </div>
  )
}
